// PBR Follow Us — horizontal image scroller with prev/next chevrons.
// Vanilla custom element, no framework, matches Horizon's web-component idiom.
//
// When the section carries a `data-feed-hash`, the tiles are filled from the
// Instafeed app's feed endpoint (the same one its own script calls), so new
// Instagram posts show up here with no merchant action. Without a hash, or if
// the fetch fails, the merchant's image blocks (shipped in a <template>) are
// the rail.

const FEED_ENDPOINT = 'https://api.nfcube.com/feed/v6';
const FEED_TIMEOUT_MS = 8000;
const FEED_CACHE_TTL_MS = 30 * 60 * 1000;
const ALT_MAX_LENGTH = 120;
const INSTAGRAM_HOSTS = ['instagram.com'];

class PbrInstagram extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.prevBtn = this.querySelector('[ref="prev"]');
    this.nextBtn = this.querySelector('[ref="next"]');

    if (!this.track || !this.prevBtn || !this.nextBtn) return;

    this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
    this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', this.updateArrows, { passive: true });
    window.addEventListener('resize', this.updateArrows, { passive: true });

    this.updateArrows();

    if (this.dataset.feedHash) this.loadFeed();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateArrows);
  }

  get step() {
    const first = this.track.querySelector('.pbr-insta__item');
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '15') || 15;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByPage(direction) {
    const perPage = Math.max(1, Math.floor(this.track.clientWidth / this.step));
    this.track.scrollBy({ left: direction * this.step * perPage, behavior: 'smooth' });
  }

  updateArrows = () => {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
    const x = this.track.scrollLeft;
    this.prevBtn.disabled = x <= 0;
    this.nextBtn.disabled = x >= maxScroll || maxScroll <= 0;
  };

  // ---- Live feed -----------------------------------------------------------

  async loadFeed() {
    const { feedHash, feedAccount, feedAlt } = this.dataset;
    const limit = Math.min(Math.max(parseInt(this.dataset.feedLimit, 10) || 12, 1), 25);

    try {
      const posts = await this.fetchPosts({ hash: feedHash, account: feedAccount, limit });
      if (!posts.length) throw new Error('Instafeed returned no posts');
      this.renderPosts(posts, feedAlt || 'View on Instagram');
    } catch (error) {
      this.renderFallback();
    }

    this.track.removeAttribute('aria-busy');
    this.track.scrollLeft = 0;
    this.updateArrows();
  }

  async fetchPosts({ hash, account, limit }) {
    const cacheKey = `pbr-insta:${account}:${hash}:${limit}`;
    const cached = readCache(cacheKey);
    if (cached) return cached;

    const url = new URL(FEED_ENDPOINT);
    url.search = new URLSearchParams({
      limit: String(limit),
      account: account || '',
      fu: '0',
      fid: '0',
      hash,
      locale: 'en',
      handle: '',
    }).toString();

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FEED_TIMEOUT_MS);
    let payload;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Instafeed responded ${response.status}`);
      payload = await response.json();
    } finally {
      clearTimeout(timer);
    }

    const items = Array.isArray(payload?.data) ? payload.data : [];
    const posts = items
      .map((item) => ({
        image: safeUrl(item?.images?.standard_resolution?.url),
        link: safeUrl(item?.link, INSTAGRAM_HOSTS),
        caption: typeof item?.caption?.text === 'string' ? item.caption.text : '',
      }))
      .filter((post) => post.image && post.link)
      .slice(0, limit);

    if (posts.length) writeCache(cacheKey, posts);
    return posts;
  }

  renderPosts(posts, fallbackAlt) {
    const fragment = document.createDocumentFragment();

    for (const post of posts) {
      const alt = captionToAlt(post.caption) || fallbackAlt;

      const item = document.createElement('li');
      item.className = 'pbr-insta__item';

      const link = document.createElement('a');
      link.className = 'pbr-insta__link';
      link.href = post.link;
      link.target = '_blank';
      link.rel = 'noopener';
      link.setAttribute('aria-label', alt);

      const img = document.createElement('img');
      img.className = 'pbr-insta__img';
      img.src = post.image;
      img.alt = alt;
      img.loading = 'lazy';
      img.width = 295;
      img.height = 390;

      link.appendChild(img);
      item.appendChild(link);
      fragment.appendChild(item);
    }

    this.track.replaceChildren(fragment);
  }

  renderFallback() {
    const template = this.querySelector('template[ref="fallback"]');
    const tiles = template ? template.content.cloneNode(true) : document.createDocumentFragment();
    this.track.replaceChildren(tiles);
  }
}

// The feed is third-party data. Only https URLs may reach href/src, and post
// links must point at Instagram, so a bad or hijacked feed can't inject a
// javascript: or foreign link into the page.
function safeUrl(value, allowedHosts) {
  if (typeof value !== 'string' || !value) return '';
  let url;
  try {
    url = new URL(value);
  } catch {
    return '';
  }
  if (url.protocol !== 'https:') return '';
  if (allowedHosts && !allowedHosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))) {
    return '';
  }
  return url.href;
}

// First line of the caption, trimmed to a sensible alt length.
function captionToAlt(caption) {
  const line = String(caption || '')
    .split('\n')
    .map((part) => part.trim())
    .find(Boolean);
  if (!line) return '';
  if (line.length <= ALT_MAX_LENGTH) return line;
  return `${line.slice(0, ALT_MAX_LENGTH - 1).trimEnd()}…`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { at, posts } = JSON.parse(raw);
    if (!Array.isArray(posts) || Date.now() - at > FEED_CACHE_TTL_MS) return null;
    return posts;
  } catch {
    return null;
  }
}

function writeCache(key, posts) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), posts }));
  } catch {
    // Storage full or unavailable — fine, we just fetch again next page.
  }
}

if (!customElements.get('pbr-instagram')) {
  customElements.define('pbr-instagram', PbrInstagram);
}
